import { useRequest } from 'ahooks'
import { useId } from 'react'
import { Optional } from 'utility-types'

import { DEFAULT_OTP_RETRY_TIMER } from '$constants'

import { toast } from '$app/utilities'

import useTimer from '$actions/useTimer'
import useOTPInput from '$actions/useOTPInput'
import useOTPBlock from '$actions/useOTPBlock'

import { asyncSuccessResolver } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'
import { MFAChallengeLarkOTPRequest } from '$services/api/types'

type UseLarkOTPOptions = FactorHookOptions

export const useLarkOTP = ({
  preferred_mfa_token_type = 'ott',
  apiInstance: {
    mfaChallengeLarkOTPRequest,
    mfaChallengeLarkOTPRetry,
    mfaChallengeLarkOTPVerify
  },
  onVerified
}: UseLarkOTPOptions) => {
  const toastID = useId()
  const otpTimer = useTimer(DEFAULT_OTP_RETRY_TIMER)

  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      resetActions()
    }
  }

  const challengeReq = useRequest(
    asyncSuccessResolver(mfaChallengeLarkOTPRequest, successResolver),
    { manual: true, onError: onApiError }
  )
  const resendReq = useRequest(
    asyncSuccessResolver(mfaChallengeLarkOTPRetry, successResolver),
    { manual: true, onError: onApiError }
  )
  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengeLarkOTPVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: res => onVerified(res.data.mfa_token)
    }
  )

  const requests = [challengeReq, resendReq, verifyReq]

  const challengeResult = challengeReq.data?.data

  const challenge_id = challengeResult?.challenge_id
  const hint = challengeResult?.hint
  const code_length = challengeResult?.code_length
  const reference_code = challengeResult?.reference_code

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const disabled = isAnyLoading

  const otpInput = useOTPInput({
    length: code_length,
    disabled,
    onFilled: (code, resetInput) => {
      if (!challenge_id) return

      const { runAsync, cancel } = verifyReq

      cancel()

      toast
        .promise(runAsync({ challenge_id, code }), {
          error: errorMessageResolver,
          loading: 'Verifying..',
          success: 'Verified.',
          id: toastID
        })
        .unwrap()
        .finally(resetInput)
    }
  })

  const otpBlock = useOTPBlock({
    otpInput,
    title: 'OTP Verification',
    description: hint || 'We have sent a code to you.',
    timerActive: otpTimer.active,
    timerSeconds: otpTimer.seconds,
    refCode: reference_code,
    onResend: () => {
      if (!challenge_id) return

      const { runAsync, cancel } = resendReq

      cancel()

      toast
        .promise(runAsync({ challenge_id }), {
          error: errorMessageResolver,
          loading: 'Resending..',
          success: 'Sent',
          id: toastID
        })
        .unwrap()
        .finally(() => {
          otpInput.resetPasswords()
          otpTimer.reset()
        })
    }
  })

  const resetActions = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
    otpInput.resetPasswords()
  }

  const reset = () => {
    resetActions()
    toast.dismiss(toastID)
  }

  const challenge = ({
    type = preferred_mfa_token_type,
    ...rest
  }: Optional<MFAChallengeLarkOTPRequest, 'type'>) => {
    const { runAsync } = challengeReq

    resetActions()
    otpTimer.reset()

    toast.promise(runAsync({ type, ...rest }), {
      error: errorMessageResolver,
      loading: 'Requesting OTP.',
      success: 'OTP requested.',
      id: toastID
    })
  }

  const reChallenge = challengeReq.refresh

  return {
    isAnyLoading,
    reset,
    otpBlock,
    challenge,
    reChallenge
  }
}

export default useLarkOTP
